import type { CaptureResult, PerformanceCaptureConfig, Properties } from 'posthog-js';

/**
 * Keep real-user monitoring to Core Web Vitals. Network timing records request
 * URLs, which is outside the browser analytics privacy contract.
 */
export const WEB_VITALS_CAPTURE_CONFIG: PerformanceCaptureConfig = {
  network_timing: false,
  web_vitals: true,
  web_vitals_allowed_metrics: ['CLS', 'FCP', 'INP', 'LCP'],
};

// These are route categories, not route values. Unknown routes deliberately
// collapse to /other so dynamic identifiers cannot become analytics data.
const PERFORMANCE_ROUTE_SEGMENTS = new Set([
  'admin',
  'ai-assistant',
  'apply',
  'artifact-viewer-demo',
  'auth',
  'bus-tracker',
  'class-chat',
  'classes-by-time',
  'classes-on-map',
  'cold-email',
  'contact',
  'course-list',
  'course-lists',
  'course-sniper',
  'courses',
  'dashboard1',
  'dashboard2',
  'dashboard3',
  'degree-planning',
  'degree-search',
  'design-system',
  'developers',
  'dining',
  'events',
  'explore',
  'forgot-password',
  'friends',
  'guides',
  'info-rutgers',
  'login',
  'oauth',
  'onboarding',
  'ontology',
  'parking',
  'parking-map',
  'prerequisite-checker',
  'privacy-policy',
  'professors',
  'profile',
  'research-opportunities',
  'research-outreach',
  'reset-password',
  'resources',
  'room-availability',
  'scheduling',
  'sirs',
  'support',
  'tos',
  'users',
]);

const SAFE_WEB_VITAL_PROPERTIES = new Set([
  'token',
  '$config_defaults',
  'distinct_id',
  '$device_id',
  '$session_id',
  '$window_id',
  '$is_identified',
  '$process_person_profile',
  '$os',
  '$os_version',
  '$browser',
  '$browser_version',
  '$browser_language',
  '$browser_language_prefix',
  '$device',
  '$device_type',
  '$timezone',
  '$timezone_offset',
  '$host',
  '$lib',
  '$lib_version',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$insert_id',
  '$time',
]);

const SAFE_METRIC_PROPERTIES = new Set([
  'name',
  'value',
  'rating',
  'delta',
  'id',
  'navigationType',
  '$session_id',
  '$window_id',
  'timestamp',
]);

const WEB_VITAL_PROPERTY = /^\$web_vitals_(CLS|FCP|INP|LCP)_(event|value)$/;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

/** Converts a browser location to a bounded, non-identifying route category. */
export function toPerformanceRoute(value: unknown): string {
  if (typeof value !== 'string') return '/other';

  try {
    const url = new URL(value, 'https://scarletsync.invalid');
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '/other';

    const firstSegment = url.pathname.split('/').filter(Boolean)[0];
    if (!firstSegment) return '/';

    return PERFORMANCE_ROUTE_SEGMENTS.has(firstSegment)
      ? `/${firstSegment}`
      : '/other';
  } catch {
    return '/other';
  }
}

function sanitizeMetric(metric: unknown, route: string): Record<string, unknown> {
  const source = asRecord(metric);
  const sanitized: Record<string, unknown> = { $current_url: route };

  if (!source) return sanitized;

  for (const [key, value] of Object.entries(source)) {
    if (SAFE_METRIC_PROPERTIES.has(key)) sanitized[key] = value;
  }

  return sanitized;
}

/**
 * PostHog's Web Vitals extension adds full URLs and browser PerformanceEntry
 * objects to its event. Restrict outbound RUM events to metric values, safe
 * device/session metadata, and a coarse route label.
 */
export function sanitizeWebVitalsCapture(capture: CaptureResult | null): CaptureResult | null {
  if (capture?.event !== '$web_vitals') return capture;

  const route = toPerformanceRoute(capture.properties.$pathname ?? capture.properties.$current_url);
  const properties: Properties = {
    $current_url: route,
    $pathname: route,
  };

  for (const [key, value] of Object.entries(capture.properties)) {
    if (SAFE_WEB_VITAL_PROPERTIES.has(key) || WEB_VITAL_PROPERTY.test(key)) {
      properties[key] = key.endsWith('_event')
        ? sanitizeMetric(value, route)
        : value;
    }
  }

  return {
    ...capture,
    properties,
    // $set/$set_once may contain initial URLs and referrers; RUM does not need
    // to create or update person properties to report a Web Vital.
    $set: undefined,
    $set_once: undefined,
  };
}

/** Keep automatic pageleaves within the same URL privacy boundary as pageviews. */
export function sanitizePostHogCapture(capture: CaptureResult | null): CaptureResult | null {
  if (capture?.event !== '$pageleave') return sanitizeWebVitalsCapture(capture);

  const properties: Properties = {};
  for (const [key, value] of Object.entries(capture.properties)) {
    if (SAFE_WEB_VITAL_PROPERTIES.has(key) || key.startsWith('$prev_pageview_') || key === '$pageview_id') {
      properties[key] = value;
    }
  }

  try {
    const url = new URL(String(capture.properties.$current_url), 'https://scarletsync.invalid');
    properties.$current_url = url.pathname;
    properties.$pathname = url.pathname;
    properties.has_search_params = Boolean(url.search);
  } catch {
    properties.$current_url = '/other';
    properties.$pathname = '/other';
  }

  return { ...capture, properties, $set: undefined, $set_once: undefined };
}
